/**
 * Ticket Template System - React Hook
 *
 * Provides template configuration for ticket forms.
 * Handles memoization and provides helper functions.
 */

import { useMemo, useCallback } from 'react'
import {
    TicketTemplate,
    TicketTemplateFields,
    TicketContext,
    ServiceDateVariant,
} from '../types'
import {
    getTemplate,
    isFieldVisible as checkFieldVisible,
    isFieldRequired as checkFieldRequired,
    isFieldDisabled as checkFieldDisabled,
    getServiceDateVariant as getVariant,
} from '../utils'

/**
 * Hook return type
 */
export interface UseTicketTemplateReturn {
    /** Complete merged template */
    template: TicketTemplate
    /** Check if a field is visible given current context */
    isFieldVisible: (
        fieldName: keyof TicketTemplateFields,
        context?: Partial<TicketContext>
    ) => boolean
    /** Check if a field is required */
    isFieldRequired: (fieldName: keyof TicketTemplateFields) => boolean
    /** Check if a field is disabled */
    isFieldDisabled: (fieldName: keyof TicketTemplateFields) => boolean
    /** Get sample content for the ticket */
    getSampleContent: () => string
    /** Get default values for the template */
    getDefaults: () => Record<string, unknown>
    /** Get service date variant */
    getServiceDateVariant: () => ServiceDateVariant
    /** Get service filter (for POC filtering) */
    getServiceFilter: () => { is_poc?: boolean } | undefined
}

/**
 * React hook for consuming ticket templates
 *
 * @param group - Ticket type group
 * @param key - Optional ticket type key
 * @returns Template configuration and helper functions
 *
 * @example
 * ```tsx
 * const { template, isFieldVisible, getDefaults } = useTicketTemplate(
 *   watch('ticket_type_group'),
 *   watch('ticket_type_key')
 * )
 *
 * // On type change, apply defaults
 * useEffect(() => {
 *   const defaults = getDefaults()
 *   Object.entries(defaults).forEach(([key, value]) => {
 *     setValue(key, value)
 *   })
 * }, [template])
 *
 * // Check field visibility
 * {isFieldVisible('marjorable', { priority: watch('ticket_priority') }) && (
 *   <TicketMarjorable />
 * )}
 * ```
 */
export function useTicketTemplate(
    group: string,
    key?: string
): UseTicketTemplateReturn {
    // Memoize template lookup
    const template = useMemo(() => {
        if (!group) {
            return getTemplate('support_request') // Default fallback
        }
        return getTemplate(group, key)
    }, [group, key])

    // Create stable context from group and key
    const baseContext = useMemo(
        (): TicketContext => ({
            group,
            key,
        }),
        [group, key]
    )

    /**
     * Check if a field is visible
     * Merges provided context with base context
     */
    const isFieldVisible = useCallback(
        (
            fieldName: keyof TicketTemplateFields,
            context?: Partial<TicketContext>
        ): boolean => {
            const fullContext: TicketContext = {
                ...baseContext,
                ...context,
            }
            return checkFieldVisible(template, fieldName, fullContext)
        },
        [template, baseContext]
    )

    /**
     * Check if a field is required
     */
    const isFieldRequired = useCallback(
        (fieldName: keyof TicketTemplateFields): boolean => {
            return checkFieldRequired(template, fieldName)
        },
        [template]
    )

    /**
     * Check if a field is disabled
     */
    const isFieldDisabled = useCallback(
        (fieldName: keyof TicketTemplateFields): boolean => {
            return checkFieldDisabled(template, fieldName)
        },
        [template]
    )

    /**
     * Get sample content
     */
    const getSampleContent = useCallback((): string => {
        return template.sampleContent
    }, [template])

    /**
     * Get default values
     */
    const getDefaults = useCallback((): Record<string, unknown> => {
        return template.defaults
    }, [template])

    /**
     * Get service date variant
     */
    const getServiceDateVariant = useCallback((): ServiceDateVariant => {
        return getVariant(template) as ServiceDateVariant
    }, [template])

    /**
     * Get service filter configuration
     */
    const getServiceFilter = useCallback(():
        | { is_poc?: boolean }
        | undefined => {
        return template.serviceFilter
    }, [template])

    return {
        template,
        isFieldVisible,
        isFieldRequired,
        isFieldDisabled,
        getSampleContent,
        getDefaults,
        getServiceDateVariant,
        getServiceFilter,
    }
}
